import { defineStore, storeToRefs } from "pinia";
import { ref, computed } from "vue";
import { deepCopy } from "@utils/toolBox";
import useMyFetch from "@stores/useMyFetch";
import { useSurveyStore } from "@stores/survey/survey";
import handleError from "@composables/handleError.js";
import { captureEvent } from "@/lib/analytics";

export const useContactsStore = defineStore("contacts", () => {
  // stores
  const { token } = storeToRefs(useSurveyStore());

  const contacts = ref([]);
  const initialContacts = ref([]);
  const deletedContacts = ref([]);
  const selectedContact = ref({});
  const selectedContactIndex = ref(-1);

  const areContactsEmpty = computed(() => contacts.value.length === 0);
  const haveContactsChanged = computed(() => JSON.stringify(contacts.value) !== JSON.stringify(initialContacts.value));
  const getContactOptions = computed(() => contacts.value.map((c) => ({ value: c.token, label: c.name })));

  const getContacts = async () => {
    try {
      const { response, data } = await useMyFetch(`/surveys/${token.value}/contacts`).json();
      if (response.value.ok) {
        const { contacts: contactsData } = data.value;
        contacts.value = contactsData;
        initialContacts.value = deepCopy(contactsData);
        selectContact(0);
      }
    } catch (err) {
      handleError(err);
    }
  };

  const deleteContact = async (contact) => {
    try {
      const { response } = await useMyFetch(`/surveys/${token.value}/contacts/${contact.token}`).delete().json();

      return response.value.ok;
    } catch (err) {
      handleError(err);
    }
  };

  const createOrUpdateContact = async (contact) => {
    const isNewContact = contact.token == null;
    try {
      if (isNewContact) {
        const { response, data } = await useMyFetch(`/surveys/${token.value}/contacts`).post({ contact }).json();
        if (response.value.ok) contact.token = data.value.token;
        return response.value.ok;
      } else {
        const { response, data } = await useMyFetch(`/surveys/${token.value}/contacts/${contact.token}`)
          .put({ contact })
          .json();
        if (response.value.ok) contact.token = data.value.token;
        return response.value.ok;
      }
    } catch (err) {
      handleError(err);
    }
  };

  const selectContact = (index) => {
    selectedContactIndex.value = index;
    selectedContact.value = contacts.value[index];
  };

  const addContact = async () => {
    const newIndex = contacts.value.length;
    const newContact = { name: `Contact ${newIndex + 1}`, custom_attributes: { test_attributes: [] } };
    contacts.value.splice(newIndex, 0, newContact);
    selectContact(newIndex);
  };

  const removeContact = async (index) => {
    const contactToDelete = contacts.value[index];
    contacts.value.splice(index, 1);
    if (contactToDelete.token != null) deletedContacts.value.push(contactToDelete);
    const newIndex = Math.min(index, contacts.value.length - 1);
    selectContact(newIndex);
  };

  const duplicateContact = async (index) => {
    const copyOfOriginalContact = deepCopy(contacts.value[index]);
    const duplicatedContact = {
      ...copyOfOriginalContact,
      name: `Copy of ${copyOfOriginalContact.name}`,
      token: null,
    };
    const newIndex = contacts.value.length;
    contacts.value.splice(newIndex, 0, duplicatedContact);
    selectContact(newIndex);
  };

  const saveContacts = async () => {
    const deletedCount = deletedContacts.value.length;
    const createdCount = contacts.value.filter((c) => c.token == null).length;

    const deleted = await Promise.all(deletedContacts.value.map(async (c) => await deleteContact(c)));
    const saved = await Promise.all(contacts.value.map(async (c) => await createOrUpdateContact(c)));
    initialContacts.value = deepCopy(contacts.value);

    if ([...deleted, ...saved].every(Boolean)) {
      captureEvent("test_contacts_saved", {
        contacts_count: contacts.value.length,
        created_count: createdCount,
        deleted_count: deletedCount,
      });
    }
  };

  const isContactSelected = (index) => selectedContactIndex.value === index;

  return {
    contacts,
    selectedContact,
    areContactsEmpty,
    haveContactsChanged,
    getContactOptions,
    getContacts,
    addContact,
    saveContacts,
    selectContact,
    removeContact,
    duplicateContact,
    isContactSelected,
  };
});
